import { state } from '@codesuma/baseline'
import settings from './settings'
import { IDayCell } from './calendar'
import {
    CatalogCountry,
    CatalogOccasion,
    CatalogOccurrence,
    OccasionType,
    occurrencesFor,
} from './occasion-catalog'
import { ICatalogCountry } from './catalog-payload'
import catalogSource from './catalog-source'
import { ICountrySubscription, shownIds } from './country-selection'
import { extraIdsFor } from './occasion-groups'

// Holds which countries the user added, and turns the catalog into the days of
// a given year.
//
// The catalog carries rules, not dates, so a year is a computation rather than a
// download — one small file per country covers every year, forever, offline.
// Where that file comes from is services/catalog-source.ts; which occasions a
// subscription actually wants is services/country-selection.ts.
//
// forDay() is synchronous, because a day slide draws synchronously. It reads
// only what is already in memory, asks for anything missing in the background,
// and broadcasts when that lands so the pages redraw.

// --- concrete dates, worked out here and remembered ---

const years = new Map<string, CatalogOccurrence[]>()
function occurrences(held: ICatalogCountry, gy: number): CatalogOccurrence[] {
    // Keyed by version too, so a catalog update is not read through a stale
    // answer computed from the payload before it.
    const key = `${held.code}/${held.version}/${gy}`
    let rows = years.get(key)
    if (!rows) {
        rows = occurrencesFor(held.definitions, held.overrides, gy)
        years.set(key, rows)
    }
    return rows
}

// One id map per country payload. The grid asks for forty-two days at a time and
// the month list asks again for each of them.
const indexes = new Map<string, Map<string, CatalogOccasion>>()
function indexOf(held: ICatalogCountry): Map<string, CatalogOccasion> {
    const key = `${held.code}/${held.version}`
    let byId = indexes.get(key)
    if (!byId) {
        byId = new Map(held.definitions.map(d => [d.id, d]))
        indexes.set(key, byId)
    }
    return byId
}

// --- reading the catalog ---

const countries = () => catalogSource.index().then(i => i?.countries ?? [])
const occasionsOf = (code: string) => catalogSource.country(code).then(c => c?.definitions ?? [])
const year = (code: string, gy: number) =>
    catalogSource.country(code).then(c => c ? occurrences(c, gy) : [])

const countryOf = (code: string): CatalogCountry | undefined =>
    catalogSource.heldIndex()?.countries.find(c => c.code === code)

// The country's own name, from whatever is already in memory. Used by the day
// slide, which cannot wait for a fetch.
const countryName = (code: string) => {
    const found = countryOf(code)
    return found?.names.fa ?? found?.names.en ?? code
}

const regionsOf = (code: string) => countryOf(code)?.regions ?? []

const topRegions = (code: string) =>
    regionsOf(code).filter(r => !r.parent).map(r => r.code)

// The occasions a region brings that the nationwide set does not.
const regionExtras = (code: string, region: string): string[] => {
    const country = countryOf(code)
    const held = catalogSource.heldCountry(code)
    return country && held ? extraIdsFor(country, held.definitions, region) : []
}

// --- what the user has added ---

const subs = (): ICountrySubscription[] => settings.get().countries
const subscription = (code: string) => subs().find(s => s.code === code)
const has = (code: string) => !!subscription(code)
const hasAll = (code: string) => {
    const sub = subscription(code)
    return !!sub && sub.regions === undefined
}
const hasRegion = (code: string, region: string) => {
    const sub = subscription(code)
    if (!sub) return false
    if (sub.regions === undefined) return true
    return sub.regions.includes(region)
}

// Which regions the country page is listing. No subscription is the nationwide
// list on its own; a subscription with no region list is every region.
const picked = (code: string): string[] | undefined => {
    const sub = subscription(code)
    return sub ? sub.regions : []
}

// What a subscription would actually put in the calendar. Empty when the
// country's catalog is not in memory, which is not the same as showing nothing.
const showing = (code: string, sub: ICountrySubscription): Set<string> => {
    const held = catalogSource.heldCountry(code)
    return held ? shownIds(held.definitions, sub, regionsOf(code)) : new Set<string>()
}

// A subscription that would show nothing is not worth keeping. A catalog that
// has not arrived counts as something, so nothing is dropped on a guess.
const showsNothing = (code: string, sub: ICountrySubscription) =>
    !!catalogSource.heldCountry(code) && !showing(code, sub).size

// Always a new value, so the state bus never mistakes a second announcement for
// the first and skips it.
let stamp = 0
const announce = () => state.set('country-occasions', ++stamp)

const write = (next: ICountrySubscription[]) => {
    settings.update({ countries: next })
    announce()
}

const replace = (code: string, next: ICountrySubscription) =>
    write(subs().map(s => s.code === code ? next : s))

function add(code: string, hidden: string[] = [], regions?: string[]) {
    if (has(code)) return
    const row: ICountrySubscription = { code, hidden }
    if (regions !== undefined) row.regions = regions
    write([...subs(), row])
    void catalogSource.country(code)
}

const remove = (code: string) => write(subs().filter(s => s.code !== code))

// Every region, including any the catalog gains later — which is what leaving
// the list off means. What was switched off by hand stays off.
const addAllRegions = (code: string) => {
    const sub = subscription(code)
    if (!sub) add(code)
    else replace(code, { code, hidden: sub.hidden })
}

// No region at all: the nationwide list on its own.
const clearRegions = (code: string) => {
    const sub = subscription(code)
    if (!sub) return
    const next = { ...sub, regions: [] }
    if (showsNothing(code, next)) remove(code)
    else replace(code, next)
}

function addRegion(code: string, region: string) {
    const sub = subscription(code)
    if (!sub) {
        add(code, [], [region])
        return
    }
    const extras = regionExtras(code, region)
    const hidden = sub.hidden.filter(id => !extras.includes(id))
    if (sub.regions === undefined) {
        replace(code, { ...sub, hidden })
        return
    }
    const regions = sub.regions.includes(region) ? sub.regions : [...sub.regions, region]
    replace(code, { ...sub, hidden, regions })
}

// A region takes its own holidays with it. A day it shares with another picked
// region — Good Friday is a holiday in ten US states — stays, because those
// regions still want it. Dropping the last region leaves the nationwide ones, so
// a country is only let go when nothing at all is left of it.
function removeRegion(code: string, region: string) {
    const sub = subscription(code)
    if (!sub) return
    const regions = (sub.regions ?? topRegions(code)).filter(r => r !== region)
    const next = { ...sub, regions }
    if (showsNothing(code, next)) remove(code)
    else replace(code, next)
}

// Will it appear in the calendar? Subscribed, and not switched off by hand,
// and in a region the subscription asked for.
const shows = (code: string, occasion: CatalogOccasion) => {
    const sub = subscription(code)
    if (!sub) return false
    const held = catalogSource.heldCountry(code)
    const defs = held?.definitions ?? [occasion]
    return shownIds(defs, sub, regionsOf(code)).has(occasion.id)
}

const allShowing = (code: string, occasions: CatalogOccasion[]) =>
    occasions.length > 0 && occasions.every(o => shows(code, o))

// One row's switch. Turning a row on while the country is off turns the country
// on and everything else off, so "just this one day" is still one tap. Turning
// off the last one that showed turns the country off rather than leaving an
// empty subscription behind.
//
// `regions` is what the page was showing when the row was tapped, and it seeds
// the subscription this writes. The page shows a country before it is
// subscribed to, so that choice is already made by the time a switch is
// touched, and a first subscription that forgot it would drop the very row that
// created it.
function setShown(code: string, id: string, on: boolean, allIds: string[], regions?: string[]) {
    const sub = subscription(code)
    if (!sub) {
        if (on) add(code, allIds.filter(other => other !== id), regions)
        return
    }
    const next: ICountrySubscription = {
        ...sub,
        hidden: on
            ? sub.hidden.filter(other => other !== id)
            : [...new Set([...sub.hidden, id])],
    }
    if (showsNothing(code, next)) remove(code)
    else replace(code, next)
}

// The switch at the top of the page. Switching off hides the ids the page is
// listing and no more, because the page lists the regions that were picked and
// the button cannot mean more than the list it sits above.
//
// Switching on drops every exception instead of only the listed ones. Un-hiding
// the list alone leaves behind an id that is not on it — a day another one
// replaces, or one belonging to a region that is not picked — and that id can
// never be reached again from this page, so "everything is added" would stop
// being true the moment a region changed. Adding a region already brings back
// what was switched off in it, so this is the same rule at country size.
function setAllShown(code: string, ids: string[], on: boolean, regions?: string[]) {
    const sub = subscription(code)
    if (!sub) {
        if (on) add(code, [], regions)
        return
    }
    const next: ICountrySubscription = {
        ...sub,
        hidden: on ? [] : [...new Set([...sub.hidden, ...ids])],
    }
    if (showsNothing(code, next)) remove(code)
    else replace(code, next)
}

// --- reading one day ---

export interface ICountryOccasion {
    country: string
    countryName: string
    names: { en: string, fa?: string, local?: string }
    type: OccasionType
    // True when this day is the day offices close rather than the legal date.
    isObserved: boolean
}

const pad = (n: number) => String(n).padStart(2, '0')

function forDay(cell: IDayCell): ICountryOccasion[] {
    const iso = `${cell.gy}-${pad(cell.gm)}-${pad(cell.gd)}`
    const out: ICountryOccasion[] = []

    for (const sub of subs()) {
        const held = catalogSource.heldCountry(sub.code)
        if (!held) {
            // Not in memory yet. Fetching announces when it lands, and the day
            // page redraws on that.
            void catalogSource.country(sub.code)
            continue
        }
        const byId = indexOf(held)
        const ids = shownIds(held.definitions, sub, regionsOf(sub.code))

        for (const occurrence of occurrences(held, cell.gy)) {
            // A holiday shows on its legal date and again on the day offices
            // close, when those differ — both are true things about the day.
            if (occurrence.date !== iso && occurrence.observedDate !== iso) continue
            const definition = byId.get(occurrence.occasionId)
            if (!definition || !ids.has(definition.id)) continue
            out.push({
                country: sub.code,
                countryName: countryName(sub.code),
                names: definition.names,
                type: definition.type,
                isObserved: occurrence.date !== iso,
            })
        }
    }
    return out
}

// Fills memory from storage before the first render, so a day opened straight
// away is drawn complete rather than filling in a moment later.
function init() {
    catalogSource.init(subs().map(s => s.code))
    // A catalog that could not be reached while offline is not gone, only
    // unreachable. Coming back online is worth another look.
    window.addEventListener('online', () => {
        void catalogSource.index()
        for (const sub of subs()) void catalogSource.country(sub.code)
    })
}

export default {
    init, countries, year, forDay,
    occasions: occasionsOf,
    subscription, has, hasAll, hasRegion, picked,
    add, addRegion, removeRegion, addAllRegions, clearRegions, remove,
    shows, setShown, setAllShown, allShowing,
}
